import type { AlreadySignedIn } from "@edu/shared";

/**
 * Тексты и выбор экрана переноса заглушки (`docs/account-adopt-design.md`,
 * раздел 9). Чистые функции, под тестами (`adopt-texts.test.ts`); разметка в
 * `AdoptConfirm.tsx`.
 *
 * ⚠️ Имена преподавателей и учеников код не склоняет: они стоят в кавычках
 * после слова, которое склоняется само («у преподавателя «Анна Петровна»»).
 * Склонять имя на глаз значит однажды написать «у Анны Петровны» про
 * «Петровну Анну», а исправлять такое человек будет не у нас, а в голове.
 *
 * ⚠️ Пустой список преподавателей опускает фразу или её часть на ВСЕХ
 * экранах (раздел 9): «занятия у преподавателей: » без имён хуже, чем ничего.
 */

/** «А»; «А» и «Б»; «А», «Б» и «В». Так список читается вслух. */
export function quoteList(names: readonly string[]): string {
  const q = names.map((n) => `«${n}»`);
  if (q.length <= 1) return q.join("");
  return `${q.slice(0, -1).join(", ")} и ${q[q.length - 1] ?? ""}`;
}

/** «преподавателя «А»» или «преподавателей «А» и «Б»» (у кого, от кого). */
export function teachersGen(names: readonly string[]): string {
  return `${names.length === 1 ? "преподавателя" : "преподавателей"} ${quoteList(names)}`;
}

/**
 * Строка последствия под каждой кнопкой, которая закрывает вход в этом
 * браузере (раздел 9, ревью 3 продукта, М6). `teachers === null` значит
 * «список неизвестен» (экран «нельзя»); пустой список читается так же: имён
 * нет, а сказать, куда возвращаться, всё равно надо.
 */
export function consequenceText(currentDisplayName: string, teachers: readonly string[] | null): string {
  const first = `В этом браузере закроется вход ученика «${currentDisplayName}».`;
  if (teachers === null || teachers.length === 0) {
    return `${first} Вернуться можно по ссылке от преподавателя, у которого занимается «${currentDisplayName}».`;
  }
  const how = teachers.length === 1 ? "по ссылке от" : "по ссылкам от";
  return `${first} Вернуться можно ${how} ${teachersGen(teachers)}.`;
}

/**
 * Вопрос 9.1: заголовок, пояснение про имена и три абзаца.
 *
 * ⚠️ Пояснение про имена обязательно, когда имена разные (ревью текстов 6, Б1):
 * каждый преподаватель вписал имя сам, «Маша» и «Мария Иванова» это норма, а
 * ребёнок, видя два разных имени, ответит «Нет», и ответ запомнится на
 * устройстве навсегда (9.4). При одинаковых именах пояснять нечего, и заголовок
 * другой: «„Маша“ и „Маша“ это один ученик?» выглядит поломкой (М10).
 */
export function offerTexts(args: {
  currentDisplayName: string;
  studentDisplayName: string;
  teacherDisplayName: string;
  currentTeachers: readonly string[];
}): { title: string; names: string | null; now: string; link: string; outcome: string } {
  const { currentDisplayName: cur, studentDisplayName: stu, teacherDisplayName: tch } = args;
  const cts = args.currentTeachers;
  const same = cur === stu;
  return {
    title: same
      ? `Ссылка для ученика «${stu}». Это тот же ученик, что уже вошёл здесь?`
      : `«${cur}» и «${stu}» это один и тот же ученик?`,
    names: same ? null : "Имя записывал каждый преподаватель сам, поэтому оно может быть написано по-разному.",
    now:
      cts.length === 0
        ? `Сейчас в этом браузере открыты занятия ученика «${cur}».`
        : `Сейчас в этом браузере открыты занятия ученика «${cur}» у ${teachersGen(cts)}.`,
    link: `Эта ссылка от преподавателя «${tch}» для ученика «${stu}».`,
    outcome:
      `Если это один ученик, в этом браузере будут занятия у ${teachersGen([...cts, tch])}: ` +
      "расписание, домашки и сообщения. Каждый преподаватель по-прежнему видит только свои занятия.",
  };
}

/**
 * Экран «нельзя» (9.3, первая строка). Без признака `openElsewhere` сюда
 * приходит и строка 9а, у которой самостоятельный путь есть ровно один: на
 * устройстве ученика ссылки открыть ссылку, по которой здесь вошёл
 * вошедший. Поэтому текст называет ИМЕННО её, а не «ссылки» вообще (ребёнок
 * открыл бы эту же ссылку у себя, и ничего бы не произошло; ревью кода 6, круг
 * 2, М1), и вторым путём преподавателя. Последняя фраза это дверь для того же
 * ученика за пять минут до урока (ревью текстов 6, Б3).
 */
export function cannotText(args: {
  currentDisplayName: string;
  studentDisplayName: string;
  teacherDisplayName: string;
  elsewhere: boolean;
}): string {
  const { currentDisplayName: cur, studentDisplayName: stu, teacherDisplayName: tch } = args;
  const same = args.elsewhere
    ? elsewhereSteps(cur, "Если это один ученик и у вас есть пароль")
    : `Если это один ученик, отсюда соединить занятия не получится. Откройте ссылку, по которой здесь открыты занятия «${cur}», ` +
      `на телефоне или компьютере, где занимается «${stu}», или скажите об этом преподавателю «${tch}».`;
  return `Если «${stu}» и «${cur}» разные люди, войдите по этой ссылке. ${same} Пока можно войти по этой ссылке отдельно.`;
}

/**
 * Путь строки 9б (ревью текстов 6, Б4): вход паролем или ссылки остальных
 * преподавателей ЗДЕСЬ, потом эта ссылка ещё раз. «Устройства, где ученик
 * занимается обычно», может не быть вовсе (ноутбук с одной ссылкой, телефон с
 * другой), поэтому единственным путём оно не названо. Имён остальных
 * преподавателей нет: держателю чужого ключа их знать незачем (14).
 *
 * ⚠️ Пароль ПЕРВЫМ (ревью кода 6, круг 2, М2): у ученика со входом сессия по
 * ссылкам не покрывает аккаунт никогда (`sessionCovers`), сколько ссылок ни
 * открой, и путь через ссылки он прошёл бы впустую.
 */
function elsewhereSteps(cur: string, lead: string): string {
  return (
    `${lead}, войдите по нему, а потом откройте эту ссылку ещё раз. ` +
    `Если пароля нет, откройте в этом браузере ссылки от остальных преподавателей ученика «${cur}», а потом эту ещё раз.`
  );
}

/** Отказ `adopt_open_elsewhere` на «Да»: тот же путь, что у экрана «нельзя здесь». */
export function elsewhereRefusalText(currentDisplayName: string): string {
  return `${elsewhereSteps(currentDisplayName, "Если у вас есть пароль")} Пока можно войти по этой ссылке отдельно.`;
}

/**
 * «Ученик ссылки недавно был на занятии» (9.3, `stubIsCurrent: false`).
 *
 * ⚠️ После урока открыть ЭТУ ЖЕ ссылку, а не ссылку первого преподавателя:
 * вход по ссылке первого из сессии заглушки упёрся бы в строку 9а таблицы 3.2,
 * если у выжившего больше одной пары.
 */
export const IN_LESSON_LINK_TEXT =
  "Можно войти на занятие отсюда. Соединить занятия получится после урока: откройте эту ссылку ещё раз.";

/**
 * Заголовок «на занятии». Без рода и без «недавно был» (ревью текстов 6, М2):
 * девочка читает про себя «был», а предикат 3.3 требует идущего урока.
 */
export function inLessonTitle(name: string): string {
  return `У ученика «${name}» сейчас идёт занятие`;
}

/** Итог 9.2 и 9.5: «Теперь в этом браузере занятия у …». `null` без имён. */
export function scopeSummary(scopeTeachers: readonly string[]): string | null {
  if (scopeTeachers.length === 0) return null;
  return `Теперь в этом браузере занятия у ${teachersGen(scopeTeachers)}.`;
}

/**
 * Какой экран рисовать на 409 `already_signed_in`.
 *
 * - `old`: прежний «Вы уже вошли». Сервер не предложил перенос, либо на этом
 *   устройстве уже ответили «Нет» этой паре (9.4);
 * - `offer`: вопрос 9.1;
 * - `cannot`: «нельзя», первая строка таблицы 9.3;
 * - `elsewhere`: «нельзя из этого браузера, пока в нём не открыты ссылки всех
 *   преподавателей ученика или не выполнен вход паролем» (строка 9б таблицы 3.2).
 */
export type ConflictScreen = "old" | "offer" | "cannot" | "elsewhere";

export function conflictScreen(info: AlreadySignedIn, remembered: boolean): ConflictScreen {
  if (info.adopt === undefined || remembered) return "old";
  if (info.adopt.verdict === "offer") return "offer";
  return info.adopt.openElsewhere === true ? "elsewhere" : "cannot";
}

/**
 * Экран 409 с учётом памяти устройства (9.4).
 *
 * ⚠️ Зовётся ОДИН раз на ответ погашения, а не на рендере (ревью кода 6, круг
 * 2, М4): память пишет сам экран («нельзя», `needs_support`), и решение,
 * пересчитанное на следующем рендере, молча подменило бы показанный экран
 * старым «Вы уже вошли».
 */
export function conflictScreenOn(store: KeyStore | null, info: AlreadySignedIn): ConflictScreen {
  const remembered =
    info.linkStudentUserId !== undefined && isRememberedNo(store, info.currentUserId, info.linkStudentUserId);
  return conflictScreen(info, remembered);
}

/**
 * Отказ подтверждения, разобранный для экрана. `null`: этот код экран
 * переноса не рисует, его показывает общий экран «Ссылка не сработала».
 */
export type AdoptRefusal =
  | { kind: "stale" }
  | { kind: "needs_support" }
  | { kind: "elsewhere" }
  | { kind: "in_lesson"; stubIsCurrent: boolean };

export function adoptRefusalOf(code: string, details: Readonly<Record<string, unknown>>): AdoptRefusal | null {
  switch (code) {
    case "adopt_stale":
      return { kind: "stale" };
    case "adopt_needs_support":
      return { kind: "needs_support" };
    case "adopt_open_elsewhere":
      return { kind: "elsewhere" };
    case "adopt_in_lesson":
      // Без признака безопаснее считать заглушкой вошедшего: тот экран не
      // ведёт в чужой урок, а главная кнопка на нём «Вернуться в кабинет»
      return { kind: "in_lesson", stubIsCurrent: details.stubIsCurrent !== false };
    default:
      return null;
  }
}

/**
 * Запоминать ли ответ на устройстве (9.4): «Нет», показанный экран «нельзя» и
 * `adopt_needs_support` (причина бывает стойкой, ревью 3 продукта, М8).
 *
 * ⚠️ «Нельзя здесь, можно там» (`elsewhere`) НЕ запоминается: его причина в
 * наборе ЭТОГО браузера, и она проходит, когда здесь откроют остальные ссылки
 * ученика. Запомненный, он навсегда спрятал бы вопрос на устройстве, где
 * ответить на него однажды станет можно.
 */
export function remembersAnswer(event: "no" | ConflictScreen | AdoptRefusal["kind"]): boolean {
  return event === "no" || event === "cannot" || event === "needs_support";
}

/** Ключ памяти по упорядоченной паре id: от порядка двух входов не зависит. */
export function adoptNoKey(a: string, b: string): string {
  return a < b ? `adopt-no:${a}:${b}` : `adopt-no:${b}:${a}`;
}

/** Хранилище памяти. Интерфейс, а не `localStorage`: тесты дают своё. */
export interface KeyStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** Доступ в `try/catch`: приватное окно бросает на записи, это штатный браузер. */
export function rememberNo(store: KeyStore | null, a: string, b: string): void {
  if (store === null) return;
  try {
    store.setItem(adoptNoKey(a, b), "1");
  } catch {
    /* хранилище не пишет: вопрос просто задастся ещё раз */
  }
}

export function isRememberedNo(store: KeyStore | null, a: string, b: string): boolean {
  if (store === null) return false;
  try {
    return store.getItem(adoptNoKey(a, b)) !== null;
  } catch {
    return false;
  }
}

/** `localStorage`, если он вообще достаётся. Доступ к свойству тоже бросает. */
export function deviceStore(): KeyStore | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}
